import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { CreditCard, Plus, Trash2, Star, Loader2, AlertCircle } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { getCurrentTenantConfig } from '../../config/tenant';
import {
  parseRedirectError,
  parseSDKError,
  formatErrorForDisplay,
  logPaymentError,
  type HyperswitchError
} from '../../lib/hyperswitchErrors';

interface PaymentMethod {
  payment_method_id: string;
  payment_method: string;
  payment_method_type?: string;
  card?: {
    last4: string;
    exp_month: string;
    exp_year: string;
    card_brand: string;
    card_holder_name?: string;
  };
  is_default: boolean;
  created_at?: string;
}

interface PaymentMethodsManagerProps {
  className?: string;
}

export const PaymentMethodsManager: React.FC<PaymentMethodsManagerProps> = ({ className }) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
  // Set when the saved default card can't take automatic (off-session)
  // charges and must be re-added — surfaced by the backend after an
  // auto-charge fails with Hyperswitch IR_39.
  const [resaveRequiredPmId, setResaveRequiredPmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  // Check for card save redirect. Trust Hyperswitch's redirect_status only —
  // our own `card_added=true` marker survives on failed redirects too, so
  // using it as a success signal produces false positives on declines.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const redirectStatus = urlParams.get('redirect_status');
    const cardAdded = urlParams.get('card_added') === 'true';

    // Only treat as success when HS explicitly confirms success
    if (status === 'succeeded' || redirectStatus === 'succeeded') {
      window.history.replaceState({}, '', window.location.pathname);
      toast.success('Card saved successfully!');
      loadPaymentMethods();
      setShowAddCard(false);
    } else if (status === 'failed' || redirectStatus === 'failed' || redirectStatus === 'requires_payment_method') {
      const parsedError = parseRedirectError(urlParams);
      window.history.replaceState({}, '', window.location.pathname);

      if (parsedError) {
        logPaymentError('Card setup redirect', parsedError);
        const errorDisplay = formatErrorForDisplay(parsedError);
        toast.error(`${errorDisplay.description}${errorDisplay.errorCode ? ` (${errorDisplay.errorCode})` : ''}`);
      } else {
        toast.error('Failed to save card. Please try again.');
      }
    } else if (cardAdded) {
      // Returned from HS with our marker but no explicit status — something
      // odd happened (SDK didn't append redirect_status). Surface as a
      // generic failure rather than claiming success.
      window.history.replaceState({}, '', window.location.pathname);
      toast.error('Card save did not complete. Please try again.');
    }
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getPaymentMethods();
      
      if (response.success && response.data) {
        setPaymentMethods(response.data.paymentMethods || []);
        setDefaultPaymentMethodId(response.data.defaultPaymentMethodId);
        setResaveRequiredPmId(response.data.resaveRequired?.paymentMethodId || null);
      } else {
        setError(response.error || 'Failed to load payment methods');
      }
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { 'payment.operation': 'load-payment-methods' }
      });
      setError(err.message || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (paymentMethodId: string) => {
    if (!confirm('Are you sure you want to remove this card?')) return;

    try {
      setDeletingId(paymentMethodId);
      const response = await apiClient.deletePaymentMethod(paymentMethodId);
      
      if (response.success) {
        toast.success('Card removed successfully');
        await loadPaymentMethods();
      } else {
        toast.error(response.error || 'Failed to remove card');
      }
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { 'payment.operation': 'delete-card' },
        extra: { paymentMethodId }
      });
      toast.error(err.message || 'Failed to remove card');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setSettingDefaultId(paymentMethodId);
      const response = await apiClient.setDefaultPaymentMethod(paymentMethodId);
      
      if (response.success) {
        toast.success('Default card updated');
        setDefaultPaymentMethodId(paymentMethodId);
        await loadPaymentMethods();
      } else {
        toast.error(response.error || 'Failed to set default card');
      }
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { 'payment.operation': 'set-default-card' },
        extra: { paymentMethodId }
      });
      toast.error(err.message || 'Failed to set default card');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleAddCardSuccess = () => {
    setShowAddCard(false);
    toast.success('Card added successfully');
    loadPaymentMethods();
  };

  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand?.toLowerCase() || '';
    if (brandLower.includes('visa')) return '💳 Visa';
    if (brandLower.includes('master')) return '💳 Mastercard';
    if (brandLower.includes('amex')) return '💳 Amex';
    if (brandLower.includes('discover')) return '💳 Discover';
    return '💳 Card';
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Loading payment methods...</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CreditCard className="h-5 w-5 mr-2 text-gray-400" />
          Payment Methods
        </h3>
        {!showAddCard && (
          <button
            onClick={() => setShowAddCard(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Card
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {resaveRequiredPmId && !showAddCard && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-800 font-medium">Automatic payments aren't working with your saved card</p>
              <p className="text-amber-700 text-sm mt-1">
                Your card was saved before we enabled automatic charging, so invoices can't be
                paid automatically — you'll keep receiving pay-by-email invoices until it's re-added.
                Re-adding takes about a minute and fixes this permanently.
              </p>
              <button
                onClick={() => setShowAddCard(true)}
                className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Re-add Card to Fix Auto-Pay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Form */}
      {showAddCard && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-3">Add New Card</h4>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> To verify your card, we'll place a temporary $1.00 authorization 
              that will automatically be released within a few days. Your card will not be charged.
            </p>
          </div>
          <AddCardForm 
            onSuccess={handleAddCardSuccess} 
            onCancel={() => setShowAddCard(false)}
          />
        </div>
      )}

      {/* Payment Methods List */}
      {paymentMethods.length === 0 && !showAddCard ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-gray-900 mb-1">No payment methods</h4>
          <p className="text-gray-500 mb-4">Add a card to enable automatic invoice payments</p>
          <button
            onClick={() => setShowAddCard(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Your First Card
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {paymentMethods.map((pm) => {
            const isDefault = pm.payment_method_id === defaultPaymentMethodId || pm.is_default;
            const isDeleting = deletingId === pm.payment_method_id;
            const isSettingDefault = settingDefaultId === pm.payment_method_id;

            return (
              <div
                key={pm.payment_method_id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  isDefault ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center">
                  <div className="text-2xl mr-3">
                    {getCardBrandIcon(pm.card?.card_brand || '')}
                  </div>
                  <div>
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">
                        •••• {pm.card?.last4 || '****'}
                      </span>
                      {isDefault && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </span>
                      )}
                      {pm.payment_method_id === resaveRequiredPmId && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Auto-pay broken — re-add
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      Expires {pm.card?.exp_month}/{pm.card?.exp_year}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!isDefault && (
                    <button
                      onClick={() => handleSetDefault(pm.payment_method_id)}
                      disabled={isSettingDefault}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      {isSettingDefault ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Set Default'
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteCard(pm.payment_method_id)}
                    disabled={isDeleting}
                    className="inline-flex items-center p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    title="Remove card"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Add Card Form Component using Orchestrator SDK (not Vault)
interface AddCardFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const AddCardForm: React.FC<AddCardFormProps> = ({ onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hyperPromiseRef = useRef<Promise<any> | null>(null);
  const tenantConfig = getCurrentTenantConfig();

  // Track if we've already initialized to prevent duplicate calls
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Initialize Hyperswitch SDK
    const initializeSDK = async () => {
      if (!hyperPromiseRef.current) {
        const hyperswitchEnv = tenantConfig.payments.hyperswitch.environment;
        const publishableKey = tenantConfig.payments.hyperswitch.publishableKey;
        
        // Determine backend URL based on environment
        const customBackendUrl = hyperswitchEnv === 'sandbox' 
          ? 'https://sandbox.hyperswitch.io'
          : 'https://api.hyperswitch.io';
        
        console.log('🔧 Hyperswitch SDK Configuration:', {
          environment: hyperswitchEnv,
          publishableKey: publishableKey?.substring(0, 20) + '...',
          customBackendUrl
        });

        try {
          hyperPromiseRef.current = loadHyper(publishableKey, {
            customBackendUrl
          });
          
          const hyperInstance = await hyperPromiseRef.current;
          console.log('✅ Hyperswitch SDK loaded:', hyperInstance ? 'OK' : 'No instance');
        } catch (sdkError: any) {
          console.error('❌ Hyperswitch SDK failed to load:', sdkError);
          Sentry.captureException(sdkError, {
            tags: { 'payment.operation': 'sdk-initialization', 'payment.component': 'AddCardForm' }
          });
          setError('Payment system initialization failed. Please refresh and try again.');
          return;
        }
      }
    };

    // Get setup intent from backend
    const getSetupIntent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        await initializeSDK();
        
        console.log('🔧 Creating setup intent for card storage...');
        const response = await apiClient.createSetupIntent();
        
        if (response.success && response.data) {
          console.log('✅ Setup intent created:', response.data.paymentIntentId);
          setClientSecret(response.data.clientSecret);
        } else {
          console.error('❌ Setup intent failed:', response.error);
          setError(response.error || 'Failed to initialize card setup');
        }
      } catch (err: any) {
        console.error('❌ Setup intent error:', err);
        Sentry.captureException(err, {
          tags: { 'payment.operation': 'create-setup-intent', 'payment.component': 'AddCardForm' }
        });
        setError(err.message || 'Failed to initialize card setup');
      } finally {
        setLoading(false);
      }
    };

    getSetupIntent();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Initializing secure card form...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (!clientSecret || !hyperPromiseRef.current) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const hyperswitchOptions = {
    publishableKey: tenantConfig.payments.hyperswitch.publishableKey,
    clientSecret,
    appearance: {
      theme: 'default' as const,
      variables: {
        colorPrimary: '#3B82F6',
        colorBackground: '#ffffff',
        colorText: '#374151',
        borderRadius: '8px'
      }
    }
  };

  return (
    <HyperElements options={hyperswitchOptions} hyper={hyperPromiseRef.current}>
      <CardSetupForm onSuccess={onSuccess} onCancel={onCancel} />
    </HyperElements>
  );
};

// Card Setup Form using UnifiedCheckout
// Note: onSuccess is handled via redirect URL, not callback (SDK flow)
const CardSetupForm: React.FC<{ onSuccess: () => void; onCancel: () => void }> = ({
  onSuccess: _onSuccess,
  onCancel
}) => {
  const [formError, setFormError] = useState<HyperswitchError | null>(null);
  const errorDisplay = formError ? formatErrorForDisplay(formError) : null;

  // UnifiedCheckout options for card setup (save card for future use)
  // Uses $1.00 auth that gets voided - card is saved via setup_future_usage
  // displaySavedPaymentMethodsCheckbox: false — no "save card?" toggle; this
  // flow exists solely to save the card, so the checkbox is redundant UI
  const unifiedCheckoutOptions = {
    displaySavedPaymentMethodsCheckbox: false,
    sdkHandleConfirmPayment: {
      handleConfirm: true,
      buttonText: 'Verify & Save Card',
      confirmParams: {
        return_url: `${window.location.origin}/settings?card_added=true`,
      },
    }
  };

  return (
    <div className="space-y-4">
      {formError && errorDisplay && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-medium text-red-800">{errorDisplay.title}</span>
              <p className="text-sm text-red-700 mt-1">{errorDisplay.description}</p>
              {errorDisplay.suggestion && (
                <p className="text-xs text-red-600 mt-1">{errorDisplay.suggestion}</p>
              )}
              {errorDisplay.errorCode && (
                <p className="text-xs text-red-500 mt-1 font-mono">Code: {errorDisplay.errorCode}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Hide Hyperswitch branding */
        [class*="powered"],
        [class*="hyperswitch"],
        [class*="Powered"],
        a[href*="hyperswitch"] {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          opacity: 0 !important;
        }
      `}</style>

      <UnifiedCheckout
        id="card-setup-checkout"
        options={unifiedCheckoutOptions}
        onReady={() => {
          console.log('✅ Card setup form loaded successfully');
          // Hide branding elements after load
          setTimeout(() => {
            const brandingElements = document.querySelectorAll('[class*="powered"], [class*="hyperswitch"], [class*="Powered"]');
            brandingElements.forEach(el => (el as HTMLElement).style.display = 'none');
          }, 500);
        }}
        onError={(error: any) => {
          const parsedError = parseSDKError(error);
          logPaymentError('CardSetupForm', parsedError);
          Sentry.captureException(error, {
            tags: {
              'payment.operation': 'card-setup-form',
              'payment.component': 'CardSetupForm',
              'payment.errorCode': parsedError.code
            },
            extra: { parsedError }
          });
          setFormError(parsedError);
        }}
      />
      
      <div className="flex justify-end pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PaymentMethodsManager;
